import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from '@chakra-ui/react'
import { deleteDoc, doc, setDoc } from 'firebase/firestore'
import { RefObject } from 'react'
import { Regimen } from '../../types/Regimen'
import useStore from '../../store/useStore'
import { getEffectiveServices } from '../../utils/firebaseServiceContext'

interface DeleteConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  confirmDelete?: {
    recordId: string
    usageIndex: number
  }
  cancelRef: RefObject<HTMLButtonElement>
  records: Regimen[]
  recordLabel: string
}

export default function DeleteConfirmDialog({
  isOpen,
  onClose,
  confirmDelete,
  cancelRef,
  records,
  recordLabel,
}: DeleteConfirmDialogProps) {
  const isImpersonating = useStore(
    (state) => state.identityStatus === 'impersonating',
  )
  const handleDelete = () => {
    if (isImpersonating) return
    if (confirmDelete) {
      const _record = records.find((r) => r.id === confirmDelete.recordId)

      if (_record) {
        const record = { ..._record }
        record.usages.splice(confirmDelete.usageIndex, 1)

        const ref = doc(
          getEffectiveServices().firestore,
          `regimens/${record.id}`,
        )

        if (record.usages.length === 0) {
          deleteDoc(ref)
        } else {
          setDoc(ref, record)
        }
      }
    }

    onClose()
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Remove {recordLabel}
          </AlertDialogHeader>

          <AlertDialogBody>
            Are you sure you want to remove this item?
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button colorScheme="gray" ref={cancelRef} onClick={onClose} mr={3}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDelete}
              isDisabled={isImpersonating}
              title={
                isImpersonating
                  ? 'Deletion is disabled while acting as a user'
                  : undefined
              }
            >
              Remove
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
